import React, { useState, useEffect, useRef } from 'react';

interface DatePickerProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  availableDates?: string[]; // Array of available dates in YYYY-MM-DD format
}

export const DatePicker: React.FC<DatePickerProps> = ({
  value,
  onChange,
  placeholder = "YY/MM/DD",
  disabled = false,
  className = "",
  availableDates = []
}) => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [currentDate, setCurrentDate] = useState<Date>(new Date());
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [displayValue, setDisplayValue] = useState<string>('');
  const calendarRef = useRef<HTMLDivElement>(null);

  // Convert YYMMDD to YY/MM/DD for display
  const formatForDisplay = (yyyymmdd: string): string => {
    if (yyyymmdd.length === 6) {
      const yy = yyyymmdd.substring(0, 2);
      const mm = yyyymmdd.substring(2, 4);
      const dd = yyyymmdd.substring(4, 6);
      return `${yy}/${mm}/${dd}`;
    }
    return yyyymmdd;
  };

  // Convert Date to YYMMDD string
  const formatDateToYYMMDD = (date: Date): string => {
    const year = date.getFullYear().toString().slice(-2);
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    return `${year}${month}${day}`;
  };

  // Convert YYMMDD string to Date
  const parseYYMMDDToDate = (yyyymmdd: string): Date | null => {
    if (yyyymmdd.length === 6) {
      const yy = parseInt(yyyymmdd.substring(0, 2));
      const mm = parseInt(yyyymmdd.substring(2, 4)) - 1; // Month is 0-indexed
      const dd = parseInt(yyyymmdd.substring(4, 6));
      const year = yy < 50 ? 2000 + yy : 1900 + yy; // Assume 00-49 = 2000s, 50-99 = 1900s
      return new Date(year, mm, dd);
    }
    return null;
  };

  // Update display value when value prop changes
  useEffect(() => {
    if (value) {
      setDisplayValue(formatForDisplay(value));
      const date = parseYYMMDDToDate(value);
      if (date) {
        setSelectedDate(date);
        setCurrentDate(date);
      }
    } else {
      setDisplayValue('');
      setSelectedDate(null);
    }
  }, [value]);

  // Close calendar when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (calendarRef.current && !calendarRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleInputClick = () => {
    if (!disabled) {
      setIsOpen(!isOpen);
    }
  };

  const handleDateSelect = (date: Date) => {
    setSelectedDate(date);
    setCurrentDate(date);
    const yyyymmdd = formatDateToYYMMDD(date);
    onChange(yyyymmdd);
    setIsOpen(false);
  };

  const navigateMonth = (direction: 'prev' | 'next') => {
    const newDate = new Date(currentDate);
    if (direction === 'prev') {
      newDate.setMonth(newDate.getMonth() - 1);
    } else {
      newDate.setMonth(newDate.getMonth() + 1);
    }
    setCurrentDate(newDate);
  };

  const getDaysInMonth = (date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const days = [];
    
    // Add empty cells for days before the first day of the month
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }
    
    // Add days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(new Date(year, month, day));
    }
    
    return days;
  };

  const isToday = (date: Date) => {
    const today = new Date();
    return date.toDateString() === today.toDateString();
  };

  const isSelected = (date: Date) => {
    return selectedDate && date.toDateString() === selectedDate.toDateString();
  };

  const isDateAvailable = (date: Date) => {
    // If no availableDates provided, all dates are available
    if (!availableDates || availableDates.length === 0) return true;
    
    // Convert date to YYYY-MM-DD format
    const dateStr = date.toISOString().split('T')[0];
    return availableDates.includes(dateStr);
  };

  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  return (
    <div className={`date-picker-container ${className}`} ref={calendarRef}>
      <input
        type="text"
        value={displayValue}
        onClick={handleInputClick}
        placeholder={placeholder}
        disabled={disabled}
        readOnly
        className={`form-input date-picker-input ${isOpen ? 'focused' : ''}`}
      />
      
      {isOpen && (
        <div className="calendar-dropdown">
          <div className="calendar-header">
            <button
              type="button"
              onClick={() => navigateMonth('prev')}
              className="calendar-nav-button"
            >
              ‹
            </button>
            <div className="calendar-title">
              {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}
            </div>
            <button
              type="button"
              onClick={() => navigateMonth('next')}
              className="calendar-nav-button"
            >
              ›
            </button>
          </div>
          
          <div className="calendar-grid">
            {dayNames.map(day => (
              <div key={day} className="calendar-day-header">
                {day}
              </div>
            ))}
            
            {getDaysInMonth(currentDate).map((date, index) => {
              const dateAvailable = date ? isDateAvailable(date) : false;
              return (
                <button
                  key={index}
                  type="button"
                  onClick={() => date && dateAvailable && handleDateSelect(date)}
                  disabled={!date || !dateAvailable}
                  className={`calendar-day ${
                    date && dateAvailable ? 'calendar-day-enabled' : 'calendar-day-disabled'
                  } ${
                    date && isToday(date) ? 'calendar-day-today' : ''
                  } ${
                    date && isSelected(date) ? 'calendar-day-selected' : ''
                  } ${
                    date && !dateAvailable ? 'calendar-day-unavailable' : ''
                  }`}
                  title={date && !dateAvailable ? 'No data available for this date' : ''}
                >
                  {date ? date.getDate() : ''}
                </button>
              );
            })}
          </div>
        </div>
      )}
      
      <div className="date-picker-help">
        Format: YY/MM/DD (e.g., 25/10/31)
      </div>
    </div>
  );
};
